"use strict";
import React, {
  createContext,
  useCallback,
  useContext,
  useRef,
  useState
} from "react";
import { assignInlineVars } from "@vanilla-extract/dynamic";
import { randomString, useLayoutEffect } from "../../../utils";
import { Label } from "../Label";
import { Text } from "../Text";
import Tooltip from "../Tooltip/Tooltip";
import classNames from "classnames";
import { Expandable } from "../../behaviors/Expandable";
import { duration } from "../../../tokens/tokens-css";
import { IconSize, Warning } from "../../icons";
import { Separator } from "../Separator/Separator";
import * as styles from "./SegmentedControl-css";
const SegmentedControlContext = createContext({});
const labelSize = /* @__PURE__ */ new Map([
  ["xs", "s"],
  ["s", "m"],
  ["m", "m"],
  ["l", "m"]
]);
export const SegmentedControl = ({
  children,
  value,
  onChange,
  label,
  labelHidden,
  size = "m",
  error,
  className,
  asymmetric = false,
  ...props
}) => {
  const [indicator, setIndicator] = useState();
  const [transitioning, setTransitioning] = useState(false);
  const id = useRef(`segmented-${randomString()}`);
  const labelId = `${id.current}-label`;
  const errorId = `${id.current}-error`;
  const optionRefs = useRef([]);
  const indicatorRef = useRef(null);
  const optionsRef = useRef(null);
  const currentOption = useRef();
  const handleKeyDown = (event) => {
    const options = optionRefs.current.filter((option) => !option.element.current?.disabled);
    const currentIndex = options.indexOf(options.find((option) => option.value === value) || options[0]);
    const { length } = options;
    const prevIndex = (currentIndex - 1 + length) % length;
    const nextIndex = (currentIndex + 1) % length;
    const prevOption = options[prevIndex];
    const nextOption = options[nextIndex];
    if (["ArrowLeft", "ArrowUp"].includes(event.key)) {
      onChange(prevOption.value);
      prevOption.element.current?.focus();
    } else if (["ArrowRight", "ArrowDown"].includes(event.key)) {
      onChange(nextOption.value);
      nextOption.element.current?.focus();
    }
  };
  const registerOption = useCallback(
    (optionRef, optionValue) => {
      if (optionValue === value) {
        currentOption.current = optionRef.current;
      }
      optionRefs.current = [...optionRefs.current, { element: optionRef, value: optionValue }];
    },
    [value]
  );
  const unregisterOption = useCallback((optionRef) => {
    optionRefs.current = optionRefs.current.filter((ref) => ref.element !== optionRef);
  }, []);
  const updateIndicator = () => {
    const containerRect = optionsRef.current?.getBoundingClientRect();
    const currentOptionRect = currentOption.current?.getBoundingClientRect();
    if (!containerRect || !currentOptionRect) return;
    setIndicator({ x: currentOptionRect.x - containerRect.x, width: currentOptionRect.width });
  };
  useLayoutEffect(() => {
    if (!currentOption.current) return;
    const resizeObserver = new ResizeObserver(updateIndicator);
    resizeObserver.observe(currentOption.current);
    updateIndicator();
    return () => {
      resizeObserver.disconnect();
    };
  }, []);
  useLayoutEffect(() => {
    setTransitioning(true);
    currentOption.current = optionRefs.current.find((item) => item.value === value)?.element.current;
    updateIndicator();
  }, [value]);
  return /* @__PURE__ */ React.createElement(
    SegmentedControlContext.Provider,
    {
      value: { optionRefs, currentValue: value, onChange, registerOption, unregisterOption, size }
    },
    /* @__PURE__ */ React.createElement(
      "div",
      {
        className: classNames(styles.container, className),
        role: "radiogroup",
        tabIndex: 0,
        "aria-labelledby": labelId,
        onKeyDown: handleKeyDown,
        "aria-invalid": !!error,
        "aria-describedby": !!error ? errorId : void 0,
        ...props
      },
      /* @__PURE__ */ React.createElement(Label, { long: true, element: "label", hidden: labelHidden, id: labelId, size: labelSize.get(size) || "m" }, label),
      /* @__PURE__ */ React.createElement("div", { className: styles.options({ size, asymmetric }), ref: optionsRef }, !!indicator && /* @__PURE__ */ React.createElement(
        "div",
        {
          ref: indicatorRef,
          className: styles.indicator({ transitioning }),
          onTransitionEnd: () => setTransitioning(false),
          style: assignInlineVars({
            [styles.indicatorLeft]: `${indicator.x}px`,
            [styles.indicatorWidth]: `${indicator.width}px`
          })
        }
      ), children),
      /* @__PURE__ */ React.createElement(Expandable, { expanded: !!error, timeout: duration.s }, ({ expandableElementRef }) => /* @__PURE__ */ React.createElement("div", { id: errorId, className: styles.error, ref: expandableElementRef, "aria-live": "polite" }, /* @__PURE__ */ React.createElement("div", { className: styles.errorIcon }, /* @__PURE__ */ React.createElement(Warning, { size: IconSize.Small, color: "critical" })), /* @__PURE__ */ React.createElement(Text, { lineHeight: "compact", size: "s" }, error)))
    )
  );
};
const optionTextSize = /* @__PURE__ */ new Map([
  ["xs", "xs"],
  ["s", "s"],
  ["m", "s"],
  ["l", "m"]
]);
export const SegmentedControlOption = ({
  value,
  icon,
  startElement,
  endElement,
  labelHidden,
  label,
  disabled,
  tooltip,
  className,
  ...props
}) => {
  const { optionRefs, currentValue, onChange, registerOption, unregisterOption, size } = useContext(SegmentedControlContext);
  const optionRef = useRef(null);
  const isSelected = currentValue === value;
  const currentIndex = optionRefs?.current?.indexOf(
    optionRefs.current.find((option) => option.value === value) || optionRefs.current[0]
  );
  const selectedIndex = optionRefs?.current?.indexOf(
    optionRefs.current.find((option) => option.value === currentValue) || optionRefs.current[0]
  ) || 0;
  const isLast = currentIndex === (optionRefs.current?.length || 0) - 1;
  const hasSeparator = !isSelected && currentIndex !== selectedIndex - 1 && !isLast;
  useLayoutEffect(() => {
    registerOption(optionRef, value);
    return () => {
      unregisterOption(optionRef, value);
    };
  }, [registerOption, unregisterOption, value]);
  const renderButton = () => /* @__PURE__ */ React.createElement(
    "button",
    {
      className: classNames(styles.button({ separator: hasSeparator }), className),
      tabIndex: isSelected ? 0 : -1,
      role: "radio",
      type: "button",
      "aria-checked": isSelected,
      "aria-label": labelHidden && typeof label === "string" ? label : void 0,
      onClick: () => onChange(value),
      ref: optionRef,
      disabled,
      ...props
    },
    !!startElement && /* @__PURE__ */ React.createElement("div", { className: styles.element }, startElement),
    !!icon && /* @__PURE__ */ React.createElement("div", { className: styles.element }, icon),
    !labelHidden && /* @__PURE__ */ React.createElement(Text, { className: styles.text, strong: true, truncate: true, size: optionTextSize.get(size) || "s", hidden: labelHidden }, label),
    !!endElement && /* @__PURE__ */ React.createElement("div", { className: styles.element }, endElement),
    /* @__PURE__ */ React.createElement(Separator, { direction: "vertical", size: "65%", className: styles.separator({ visible: hasSeparator }) })
  );
  if (!!tooltip) {
    return /* @__PURE__ */ React.createElement(Tooltip, { ...tooltip }, renderButton());
  }
  return renderButton();
};
